/**
 * Shapes for the financial-statement screens — Balance Sheet and Profit & Loss.
 *
 * These are **domain** shapes, not wire shapes. The live bos-api statement
 * endpoints (`/ledger/statements/{layout,balance-sheet,profit-and-loss,plausibility}`)
 * name almost every field differently; `statementsMapping` is the single seam
 * that translates, and it is the only file that knows the server's names.
 *
 * The one rule these types encode: **statement layout is data, not code.** The
 * backend returns a flat, depth-ordered list of {@link StatementLine}s carrying
 * their own label, sign, depth and — crucially — their already computed amount,
 * comparative and delta. The renderer draws whatever list it is given; it never
 * hardcodes sections, order, or the account→line mapping, and it never
 * recomputes a subtotal, a total or a delta. Re-seed the layout server-side and
 * the screen changes with it, with no client edit.
 *
 * Money follows the project contract: integer **tetri** (1 GEL = 100 tetri) for
 * arithmetic, decimal strings for display echoes. Never sum the decimals — see
 * {@link ../../utils/money/formatGel}.
 */

import type { AccountType, LocaleCode } from './Accounts';

/** Which statement a payload describes. */
export type StatementKind = 'BALANCE_SHEET' | 'PROFIT_AND_LOSS';

/**
 * A line's role in the statement — and, decisively, whether it has a
 * composition to drill into.
 *
 * - `ACCOUNTS` — composed from posted account balances. Carries
 *   {@link StatementLine.accounts}, so it drills.
 * - `SUBTOTAL` — a rollup of descendants. **No composition.**
 * - `COMPUTED` — a derived figure (the result for the period). **No
 *   composition.**
 *
 * A line with no composition must not render a drill affordance: an affordance
 * that goes nowhere is exactly the dead link this integration removes.
 */
export type StatementLineKind = 'ACCOUNTS' | 'SUBTOTAL' | 'COMPUTED';

/** Whether the statement carries prior-period comparatives. */
export type StatementComparative = 'NONE' | 'PRIOR_YEAR';

/**
 * The line's **presentation** multiplier — `-1` for a line that reads as a
 * deduction (a contra account, an expense block netted into its parent).
 *
 * It applies to display only. {@link StatementLine.amountTetri} and
 * {@link StatementLine.deltaTetri} are both in the line's own **natural** terms
 * with this sign excluded, which is what makes accumulated depreciation growing
 * from 11,577 to 19,000 read as growth rather than as a decrease.
 */
export type StatementLineSign = 1 | -1;

/**
 * One account's contribution to a line — the data behind the drill-through.
 * `accountId` is a **real ledger account id**: it is what the journal's
 * `?account=` filter resolves against.
 */
export type StatementLineAccount = {
    accountId: string;
    accountCode: string;
    /** Already resolved to the requested locale by the server. */
    accountName: string;
    /** Display echo of `amountTetri` (2 dp). */
    amount: string;
    /** Natural terms, integer tetri — presentation sign excluded. */
    amountTetri: number;
};

/**
 * A single row of the statement, in render order. `depth` describes the
 * hierarchy (the wire carries no parent link on a rendered line — the layout
 * endpoint has that, and nothing on these screens needs it).
 *
 * `label` arrives already resolved to the requested locale; the client does not
 * own statement wording.
 */
export type StatementLine = {
    id: string;
    label: string;
    depth: number;
    kind: StatementLineKind;
    sign: StatementLineSign;
    /** Display echo of `amountTetri` (2 dp), natural terms. */
    amount: string;
    /**
     * The already-computed figure, integer tetri, in the line's **natural**
     * terms. Never recomputed client-side; never re-signed.
     */
    amountTetri: number;
    /** The same line in the comparative window; `null` when there is none. */
    priorAmount: string | null;
    priorAmountTetri: number | null;
    /**
     * `current − prior`, **computed by the server** in the line's own natural
     * terms with the presentation sign excluded.
     *
     * Do not recompute this and do not apply {@link StatementLine.sign} to it.
     * Both were the cause of the contra delta reading backwards, and either one
     * reintroduces it.
     */
    deltaAmount: string | null;
    deltaTetri: number | null;
    /**
     * The accounts making up this line. **Empty for `SUBTOTAL` and `COMPUTED`** —
     * those lines have no composition and must not offer a drill affordance.
     */
    accounts: StatementLineAccount[];
};

/**
 * The window a statement (or its comparative column) covers. A Balance Sheet is
 * as-of a date and carries `asOf`; a Profit & Loss spans a range and carries
 * `from`/`to`. Exactly one shape is populated.
 *
 * The comparative column's header is rendered **from this**, never from a
 * hardcoded string — a Balance Sheet is a position, not a period, so labelling
 * its columns "this period" is wrong.
 */
export type StatementWindow = {
    asOf: string | null;
    from: string | null;
    to: string | null;
};

/**
 * A postable account the statement layout does not map anywhere, so its balance
 * appears in no line. Reported alongside the statement, and again as an
 * `UNMAPPED_ACCOUNT` plausibility warning.
 */
export type UnmappedAccount = {
    accountId: string;
    accountCode: string;
    accountName: string;
    type: AccountType;
};

/** A statement, ready to render. */
export type Statement = {
    kind: StatementKind;
    /** The locale the server resolved labels into. */
    locale: LocaleCode;
    /** What this statement covers. */
    window: StatementWindow;
    comparative: StatementComparative;
    /** What the comparative column covers; `null` when there is no comparative. */
    comparativeWindow: StatementWindow | null;
    lines: StatementLine[];
    unmappedAccounts: UnmappedAccount[];
};

/**
 * An advisory plausibility notice — informational, never an error, never
 * blocking. Served by the separate `/plausibility` call; a statement response
 * carries no warnings of its own.
 *
 * `rule` is an open, machine-readable code (`ASSETS_NE_EQLIAB`,
 * `UNMAPPED_ACCOUNT`, …). Branch on it only where behaviour must differ;
 * `message` is already localized and is what the banner shows.
 */
export type PlausibilityWarning = {
    rule: string;
    /**
     * The server's English sentence.
     *
     * **Deprecated (BOS-163)** — kept because the API still sends it for one
     * release, and because an unrecognised rule has nothing else to show. A
     * recognised rule is written from `rule` + the structured fields below,
     * through this app's own locale files and `formatGel`; the server's prose
     * was English-only and quoted the account by chart code, so a Georgian
     * reader got "Asset '1120' has a credit natural balance (-100)." on an
     * otherwise Georgian page.
     */
    message: string;
    lineId: string | null;
    accountId: string | null;
    accountCode: string | null;
    /** The account's name in the requested locale. */
    accountName: string | null;
    /** The figure the rule is about, integer tetri; `null` when it is not about one. */
    amountTetri: number | null;
};

/** The plausibility rule that says the Balance Sheet does not balance. */
export const ASSETS_NE_EQLIAB = 'ASSETS_NE_EQLIAB';

/** The advisory set for a statement, from the separate `/plausibility` call. */
export type PlausibilityReport = {
    asOf: string | null;
    warnings: PlausibilityWarning[];
};

/**
 * The Balance Sheet balance check — total assets vs. total equity + liabilities.
 * A comparison of two server-reported totals, not a recomputation of the
 * statement; modelled on the journal entry form's balance indicator.
 */
export type BalanceCheck = {
    assetsTetri: number;
    equityLiabilitiesTetri: number;
    /** `assetsTetri - equityLiabilitiesTetri` (0 when balanced). */
    differenceTetri: number;
    balanced: boolean;
};

/** `GET /ledger/statements/balance-sheet?asOf=…` */
export type GetBalanceSheetResponse = Statement;

/** `GET /ledger/statements/profit-and-loss?dateFrom=…&dateTo=…` */
export type GetProfitAndLossResponse = Statement;

/** `GET /ledger/statements/plausibility?asOf=…` */
export type GetPlausibilityResponse = PlausibilityReport;
